'use client';

import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Text,
  Icon,
  HStack,
  Spinner,
  Center,
  Box,
  Stack,
  VStack,
  Button,
} from '@chakra-ui/react';
import { ICONS } from '~/constants';
import { useUIStore } from '~/store/useUIStore';
import { useEffect, useState, useRef } from 'react';
import { getAPI, patchAPI } from '~/lib/api';
import { useToast } from '~/lib/toasts';
import { useRouter } from 'next/navigation';
import type { TODO, NestedSearch } from '@habity/types';
import type { AutocompleteItem } from '@habity/types/autocomplete';
import { useHomesStore } from '~/store/useHomesStore';
import { AutocompleteInput } from '../forms/Autocomplete';
import { Filters } from '../forms/search/Filters';
import { revalidateBuyerDashboard } from '~/app/actions/revalidateBuyerDashboard';
import { useUserAction } from '~/hooks/useUserAction';

export interface PropertySearchFlyoutProps {
  onSaveSuccess?: () => void;
}

export const PropertySearchFlyout = ({
  onSaveSuccess,
}: PropertySearchFlyoutProps) => {
  const modals = useUIStore((state) => state.modals);
  const setModals = useUIStore((state) => state.setModals);
  const editSearchId = useUIStore((state) => state.editSearchId);
  const setEditSearchId = useUIStore((state) => state.setEditSearchId);
  const createSearchLocations = useUIStore(
    (state) => state.createSearchLocations
  );
  const setCreateSearchLocations = useUIStore(
    (state) => state.setCreateSearchLocations
  );
  const { showToast } = useToast();
  const router = useRouter();
  const { executeAction, isLoading: isCreatingSearch } = useUserAction();

  const isCreateMode = !editSearchId && !!createSearchLocations;

  // Global store access
  const filters = useHomesStore((state) => state.filters);
  const setFilters = useHomesStore((state) => state.setFilters);
  const locations = useHomesStore((state) => state.locations);
  const setLocations = useHomesStore((state) => state.setLocations);

  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isReady, setIsReady] = useState(false);

  // Store previous filters/locations to restore on close
  const previousFiltersRef = useRef<Partial<NestedSearch> | null>(null);
  const previousLocationsRef = useRef<AutocompleteItem[]>([]);

  const getSearchData = () => {
    const currentFilters = useHomesStore.getState().filters;
    const currentLocations = useHomesStore.getState().locations;
    return {
      ...currentFilters,
      areaIds:
        currentLocations
          ?.filter((l: AutocompleteItem) => l.type === 'area')
          .map((l: AutocompleteItem) => Number(l.id)) || [],
      municipalityIds:
        currentLocations
          ?.filter((l: AutocompleteItem) => l.type === 'municipality')
          .map((l: AutocompleteItem) => Number(l.id)) || [],
    };
  };

  const resetAndClose = ({ restorePrevious = true } = {}) => {
    if (restorePrevious) {
      setFilters(previousFiltersRef.current);
      setLocations(previousLocationsRef.current);
    } else {
      previousFiltersRef.current = null;
      previousLocationsRef.current = [];
      setFilters(null);
      setLocations([]);
    }
    const currentModals = useUIStore.getState().modals;
    setModals({ ...currentModals, propertySearchOpen: false });
    setEditSearchId(null);
    setCreateSearchLocations(null);
    setIsReady(false);
  };

  const onClose = () => resetAndClose();

  // Create mode: populate global store with initial data
  useEffect(() => {
    if (!isCreateMode || !modals.propertySearchOpen) return;

    // Save current state to restore on close
    previousFiltersRef.current = filters;
    previousLocationsRef.current = locations;

    setLocations(createSearchLocations || []);
    setFilters(null);
    setIsReady(true);
  }, [isCreateMode, modals.propertySearchOpen]);

  // Edit mode: load search data and populate global store when editSearchId changes
  useEffect(() => {
    const loadSearchData = async () => {
      if (!editSearchId || !modals.propertySearchOpen) {
        if (!isCreateMode) setIsReady(false);
        return;
      }

      // Save current filters/locations before overwriting
      previousFiltersRef.current = filters;
      previousLocationsRef.current = locations;

      setIsLoadingSearch(true);
      try {
        const response = await getAPI(
          `/providers/supabase/searches/${editSearchId}`
        );
        if (response?.data) {
          const searchData = response.data;

          // Build locations from areas and municipalities
          const searchLocations: AutocompleteItem[] = [
            ...(searchData.areas?.map((a: TODO) => ({
              id: a.id?.toString() || '',
              name: a.name || '',
              type: 'area' as const,
            })) || []),
            ...(searchData.municipalities?.map((m: TODO) => ({
              id: m.id?.toString() || '',
              name: m.name || '',
              type: 'municipality' as const,
            })) || []),
          ];

          // Build filters from search data
          const searchFilters: Partial<NestedSearch> = {
            categories: searchData.categories || null,
            price_min: searchData.price_min ?? null,
            price_max: searchData.price_max ?? null,
            rooms_min: searchData.rooms_min ?? null,
            rooms_max: searchData.rooms_max ?? null,
            living_space_min: searchData.living_space_min ?? null,
            living_space_max: searchData.living_space_max ?? null,
            bedrooms_min: searchData.bedrooms_min ?? null,
            bedrooms_max: searchData.bedrooms_max ?? null,
            build_year_min: searchData.build_year_min ?? null,
            build_year_max: searchData.build_year_max ?? null,
            monthly_fee_min: searchData.monthly_fee_min ?? null,
            monthly_fee_max: searchData.monthly_fee_max ?? null,
            plot_size_min: searchData.plot_size_min ?? null,
            plot_size_max: searchData.plot_size_max ?? null,
            features: searchData.features || null,
            nearby: searchData.nearby || null,
          };

          // Populate global store
          setLocations(searchLocations);
          setFilters(searchFilters);
          setIsReady(true);
        }
      } catch (error) {
        console.error('Error loading search:', error);
        showToast(
          {
            title: 'Kunde inte ladda bevakningen',
            description: 'Försök igen senare',
          },
          { status: 'error' }
        );
        onClose();
      } finally {
        setIsLoadingSearch(false);
      }
    };

    loadSearchData();
  }, [editSearchId, modals.propertySearchOpen]);

  const handleCreate = async () => {
    try {
      await executeAction({
        action: 'created_search_profile',
        searchFilters: getSearchData(),
      });
      resetAndClose({ restorePrevious: false });
      onSaveSuccess?.();
    } catch (error) {
      console.error('Error creating search:', error);
      showToast(
        {
          title: 'Kunde inte skapa bevakningen',
          description: 'Försök igen senare',
        },
        { status: 'error' }
      );
    }
  };

  const handleSave = async () => {
    if (!editSearchId) return;

    setIsSaving(true);
    try {
      await patchAPI(
        `/providers/supabase/searches/${editSearchId}`,
        JSON.stringify(getSearchData())
      );

      showToast(
        {
          title: 'Bevakningen uppdaterad',
          description: 'Dina ändringar har sparats',
        },
        { status: 'success' }
      );

      resetAndClose({ restorePrevious: false });
      onSaveSuccess?.();
      revalidateBuyerDashboard();
      router.refresh();
    } catch (error) {
      console.error('Error saving search:', error);
      showToast(
        { title: 'Kunde inte spara', description: 'Försök igen senare' },
        { status: 'error' }
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Drawer
      isOpen={modals.propertySearchOpen}
      placement="right"
      onClose={onClose}
      size={{ mobile: 'full', desktop: 'md' }}
      closeOnOverlayClick={false}
    >
      <DrawerOverlay />
      <DrawerContent display="flex" flexDirection="column" maxH="100vh">
        <DrawerCloseButton
          size="lg"
          top="4"
          right="4"
          _hover={{
            backgroundColor: 'blackAlpha.200',
          }}
        />
        <DrawerHeader
          borderBottomWidth="1px"
          borderBottomColor="blackAlpha.200"
          py="6"
          flexShrink={0}
        >
          <HStack spacing="3">
            <Icon as={ICONS.EDIT_HOME} boxSize="24px" color="blackAlpha.700" />
            <Text fontSize="xl" fontWeight="semibold" color="blackAlpha.900">
              {isCreateMode ? 'Skapa bevakning' : 'Redigera bevakning'}
            </Text>
          </HStack>
        </DrawerHeader>

        <DrawerBody
          px="0"
          py="0"
          overflowY="auto"
          flex="1"
          display="flex"
          flexDirection="column"
        >
          {isLoadingSearch ? (
            <Center flex="1" py="12">
              <Spinner size="lg" color="blackAlpha.600" />
            </Center>
          ) : isReady ? (
            <Box display="flex" flexDirection="column" flex="1" minH="0">
              <VStack spacing="0" align="stretch" flex="1" overflowY="auto">
                <Box
                  px="6"
                  py="6"
                  borderBottomWidth="1px"
                  borderBottomColor="blackAlpha.100"
                  backgroundColor="white"
                >
                  <Stack spacing="4">
                    <Box>
                      <AutocompleteInput
                        tab="homes"
                        ignoreSearch
                        showGlobalLocations
                        customPlaceholder="Kommun, område eller gata"
                        containerProps={{ mb: 0 }}
                      />
                    </Box>

                    <Filters ignoreSearch />
                  </Stack>
                </Box>
              </VStack>

              <Box
                px="6"
                pt="4"
                pb={{ base: '6', desktop: '4' }}
                backgroundColor="white"
                borderTopWidth="1px"
                borderTopColor="blackAlpha.200"
                flexShrink={0}
                zIndex={10}
                position="relative"
                paddingBottom={{
                  base: 'max(24px, env(safe-area-inset-bottom))',
                  desktop: '16px',
                }}
              >
                <VStack spacing="3">
                  <Button
                    onClick={isCreateMode ? handleCreate : handleSave}
                    width="full"
                    isLoading={isCreateMode ? isCreatingSearch : isSaving}
                    loadingText={
                      isCreateMode ? 'Skapar bevakning...' : 'Sparar...'
                    }
                    size="lg"
                    bg="blackAlpha.900"
                    color="white"
                    _hover={{ bg: 'blackAlpha.800' }}
                  >
                    {isCreateMode ? 'Skapa bevakning' : 'Spara ändringar'}
                  </Button>
                  <Button
                    variant="ghost"
                    width="full"
                    onClick={onClose}
                    isDisabled={isSaving}
                    size="lg"
                    color="blackAlpha.700"
                  >
                    Avbryt
                  </Button>
                </VStack>
              </Box>
            </Box>
          ) : (
            <Center flex="1" py="12">
              <Text color="blackAlpha.600">Ingen bevakning vald</Text>
            </Center>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
